'use client'

import useDetectScroll, { Direction } from '@smakss/react-scroll-direction'
import { useDeviceType } from 'hooks/useDeviceType'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import styles from './Values.module.scss'

const perksSupportive = [
  {
    title: 'Cozy Office',
    description: 'Fully equipped kitchen, and the tools you need to stay productive',
  },
  {
    title: 'Relaxed Atmosphere',
    description: 'A collaborative, creative, and welcoming environment',
  },
  {
    title: 'Open Vacation Policy',
    description: 'Take time off whenever you need it, as long as your work is on track',
  },
  {
    title: 'Flexible Working Hours',
    description: 'We support whatever schedule works best for you',
  },
  {
    title: 'Student Vacation Policy',
    description: 'On student contracts we offer 10 paid days per year',
  },
  {
    title: 'Regular Check-Ins',
    description: "For a chat or a career plan, we've got you covered",
  },
  {
    title: 'Home Office Day',
    description: 'We offer one work-from-home day a week',
  },
  {
    title: 'Open Conversations',
    description: 'We encourage everyone to share any concerns',
  },
]

const perksThoughtful = [
  {
    title: 'Holiday Bonuses',
    description: 'Helping you enjoy the most wonderful times of the year',
  },
  {
    title: 'Performance Bonus',
    description: 'The amount differs based on personal engagement',
  },
  {
    title: 'Baby Bonus',
    description: 'We celebrate new parents with a little something extra',
  },
  {
    title: 'Kindergarten Compensation',
    description: 'We cover your monthly kindergarten fees',
  },
  {
    title: 'Hardware Budget',
    description: 'Choose any item that will make your work life easier',
  },
  {
    title: 'Paid Parking Space',
    description: "We've got a secure parking spot nearby just for you",
  },
  {
    title: "Finder's Fee",
    description: "Help us recruit a good fit for the team, and we' ll reward you for it",
  },
  {
    title: 'MultiSport Card',
    description: 'Access to most activities (gyms, pools, tennis fields...) in the country',
  },
  {
    title: 'Education Budget',
    description: 'Use your annual budget for conferences or courses that spark your interest',
  },
  {
    title: 'Extra Voluntary Pension Payments',
    description: 'Boost your retirement savings with contributions to the third pension pillar',
  },
  {
    title: 'Additional Medical Insurance',
    description: 'Annual full physical exam with benefits for control check-ups',
  },
  {
    title: 'Team Fun Budget',
    description: 'We love to come together for smaller gatherings throughout the year',
  },
]

const perksProfessional = [
  {
    title: 'Global Reach Projects',
    description: 'Take on exciting projects with international clients',
  },
  {
    title: 'Flat Structure',
    description: 'We trust our team to take ownership of their work',
  },
  {
    title: 'Simple Employment Process',
    description: 'One round, rare homework, decision in seven days',
  },
  {
    title: 'Onboarding & Mentorship Program',
    description: 'New members get support and a mentor',
  },
  {
    title: 'Working Resources',
    description: 'We set you up with resources from project guidelines to code review strategies',
  },
  {
    title: 'Learning Resources',
    description: 'Access our learning platform and request resources to continue to grow',
  },
  {
    title: 'Take a Break and Learn Something New',
    description: 'Every Friday, enjoy 2 hours to explore new skills in Hokus Pokus',
  },
  {
    title: 'Weekly Knowledge Sharing Presentations',
    description: 'We enjoy sharing knowledge on new topics',
  },
]

const Values = () => {
  const [currentSection, setCurrentSection] = useState('support')
  const supportRef = useRef<HTMLDivElement>(null)
  const thoughtfullRef = useRef<HTMLDivElement>(null)
  const professionalRef = useRef<HTMLDivElement>(null)
  const { scrollDir, scrollPosition } = useDetectScroll()
  const { isMobile, isTablet } = useDeviceType()

  function notDesktop() {
    return isMobile || isTablet
  }

  useEffect(() => {
    function handleScroll() {
      if (supportRef.current && thoughtfullRef.current && professionalRef.current) {
        const supportRect = supportRef.current.getBoundingClientRect()
        const thoughtfullRect = thoughtfullRef.current.getBoundingClientRect()
        const professionalRect = professionalRef.current.getBoundingClientRect()

        if (scrollDir === Direction.Down) {
          if (supportRect.top > 250 && supportRect.top < 350) {
            setCurrentSection('support')
          } else if (thoughtfullRect.top > 250 && thoughtfullRect.top < 350) {
            setCurrentSection('thoughtfull')
          } else if (professionalRect.top > 250 && professionalRect.top < 350) {
            setCurrentSection('professional')
          }
        } else if (scrollDir === Direction.Up) {
          if (supportRect.bottom > 350 && supportRect.bottom < 450) {
            setCurrentSection('support')
          } else if (thoughtfullRect.bottom > 350 && thoughtfullRect.bottom < 450) {
            setCurrentSection('thoughtfull')
          } else if (professionalRect.bottom > 350 && professionalRect.bottom < 450) {
            setCurrentSection('professional')
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrollDir, scrollPosition.top])

  return (
    <section className={styles.container}>
      <div ref={supportRef} className={styles.perks + ' ' + styles.support}>
        <div className={styles['perks-header']}>
          <span className={styles['perks-header-smalltext']}>Our Values</span>
          <span className={styles['perks-header-title']}>Support is at the heart{notDesktop() ? ' ' : <br />}of everything we do</span>
          {notDesktop() && <div className={styles['mobile-image']} />}
        </div>
        <div className={styles['perks-bullets']}>
          {perksSupportive.map((perk) => (
            <div key={perk.title} className={styles.perk}>
              <span className={styles['perk-title']}>{perk.title}</span>
              <span className={styles['perk-text']}>{perk.description}</span>
            </div>
          ))}
        </div>
      </div>

      <div ref={thoughtfullRef} className={styles.perks + ' ' + styles.thoughtfull}>
        <div className={styles['perks-header']}>
          <span className={styles['perks-header-smalltext']}>Our Values</span>
          <span className={styles['perks-header-title']}>Thoughtfulness guides how{notDesktop() ? ' ' : <br />}we interact and collaborate</span>
          {notDesktop() && <div className={styles['mobile-image']} />}
        </div>
        <div className={styles['perks-bullets']}>
          {perksThoughtful.map((perk) => (
            <div key={perk.title} className={styles.perk}>
              <span className={styles['perk-title']}>{perk.title}</span>
              <span className={styles['perk-text']}>{perk.description}</span>
            </div>
          ))}
        </div>
      </div>

      <div ref={professionalRef} className={styles.perks + ' ' + styles.professional}>
        <div className={styles['perks-header']}>
          <span className={styles['perks-header-smalltext']}>Our Values</span>
          <span className={styles['perks-header-title']}>We value professionalism through respect and dedication</span>
          {notDesktop() && <div className={styles['mobile-image']} />}
        </div>
        <div className={styles['perks-bullets']}>
          {perksProfessional.map((perk) => (
            <div key={perk.title} className={styles.perk}>
              <span className={styles['perk-title']}>{perk.title}</span>
              <span className={styles['perk-text']}>{perk.description}</span>
            </div>
          ))}
        </div>
      </div>

      {!notDesktop() && (
        <AnimatePresence>
          <motion.div
            key={currentSection}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={styles['desktop-image'] + ' ' + styles[currentSection]}
          />
        </AnimatePresence>
      )}
    </section>
  )
}

export default Values
